import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OAuthStorageService } from '@qaroni-app/auth/services/o-auth-storage.service';
import { CommonsHttpService } from '@qaroni-core/services/commons/commons-http/commons-http.service';
import { Observable, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { UserRole } from '../types/user-role';
import { UserRoleCache } from '../types/user-role-cache.class';
import { UserRoleApiResponse } from '../types/user-role-response';
import { AllAdminRoles, UserRoleEnum } from '../types/user-role.enum';
import { WorkersHttpService } from './workers-http.service';

@Injectable({
  providedIn: 'root',
})
export class WorkersService {
  private commonsHttp = inject(CommonsHttpService);
  private http = inject(WorkersHttpService);
  private oAuthStorage = inject(OAuthStorageService);

  protected readonly userRoleSubject = new Subject<UserRole>();
  private userRole: UserRoleCache = new UserRoleCache();

  // ==========================================================================================
  // Check company for redirect
  // ==========================================================================================

  public checkCompanyForRedirect(
    companyID: number,
    role: UserRoleEnum,
    employeeID?: number
  ): string[] {
    const urlToDashboard = `/companies/${companyID}`;
    if (!employeeID) {
      return [`${urlToDashboard}/landing`];
    } else {
      if (AllAdminRoles.some(adminRole => adminRole === role)) {
        return [`${urlToDashboard}/landing`];
      } else {
        return [`${urlToDashboard}/panel-landing`];
      }
    }
  }

  // ==========================================================================================
  // Get User Role
  // ==========================================================================================

  public getUserRole$(): Observable<UserRole> {
    return this.userRoleSubject.asObservable();
  }

  public getUserRole(companyID: number, userID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    if (this.userRole.canReturn(companyID, userID)) {
      this.userRoleSubject.next(this.userRole.get());
    } else if (this.userRole.canApi(companyID, userID)) {
      this.userRole.startToFly();
      this.http
        .getUserRole$(companyID, userID)
        .pipe(finalize(() => this.userRole.endFlying()))
        .subscribe({
          next: this.nextGetUserRole,
          error: this.errorGetUserRole,
        });
    }
  }

  private nextGetUserRole = (data: UserRoleApiResponse): void => {
    if (
      this.commonsHttp.validationsHttp.verifyStatus200(data) &&
      data?.body?.result?.length
    ) {
      this.userRole.set(data.body.result[0]);
      this.userRoleSubject.next(this.userRole.get());
    } else {
      this.userRoleSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorGetUserRole = (error: HttpErrorResponse): void => {
    this.userRoleSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  public getUserRoleHttp$(
    companyID: number,
    userID: number
  ): Observable<UserRoleApiResponse> {
    return this.http.getUserRole$(companyID, userID);
  }
}
